import { useQuery } from '@tanstack/react-query'
import { fetchFreelancers } from '@/apis/freelancers'
import { useTeamScopeApiReady } from '@/hooks/useApiReady'

// Query keys
export const freelancersListKeys = {
  all: ['freelancers'] as const,
  lists: () => [...freelancersListKeys.all, 'list'] as const,
  list: (filters: any) => [...freelancersListKeys.lists(), filters] as const,
}

export interface FreelancersListFilters {
  status?: string | string[]
  search?: string
  team_tag_id?: string | string[]
  referent_id?: string
  team_id?: string
  job_title?: string
  team_unit_ids?: string[]
  kyc_complete?: 'complete' | 'incomplete'
  master_agreement?: 'active' | 'inactive'
  freelancer_type?: 'auto-entrepreneur' | 'company' | 'foreign-company' | 'portage' | 'umbrella' | 'esn'
  /** Aligné sur l’API missions : created_at | freelancer | status */
  electronic_invoicing?: 'ok' | 'not_ok' | 'not_applicable'
  /** Favoris personnels de l'utilisateur courant */
  is_favorite?: boolean
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

function buildFreelancersListQueryFn(filters: FreelancersListFilters) {
  return async () => {
    const statusParam =
      Array.isArray(filters.status) && filters.status.length > 0
        ? filters.status.join(',')
        : filters.status || null

    const params: any = {
      status: statusParam,
      search: filters.search,
    }

    if (Array.isArray(filters.team_tag_id) && filters.team_tag_id.length > 0) {
      const tagIds = filters.team_tag_id.filter(Boolean)
      if (tagIds.length > 0) {
        params.team_tag_id = tagIds.join(',')
      }
    } else if (filters.team_tag_id) {
      params.team_tag_id = filters.team_tag_id
    }

    if (filters.referent_id) {
      params.referent_id = filters.referent_id
    }

    if (filters.team_id !== undefined) {
      params.team_id = filters.team_id
    }

    if (filters.job_title) {
      params.job_title = filters.job_title
    }
    if (filters.team_unit_ids && filters.team_unit_ids.length > 0) {
      params.team_unit_ids = filters.team_unit_ids.join(',')
    }
    if (filters.kyc_complete) {
      params.kyc_complete = filters.kyc_complete
    }
    if (filters.master_agreement) {
      params.master_agreement = filters.master_agreement
    }
    if (filters.freelancer_type) {
      params.freelancer_type = filters.freelancer_type
    }
    if (filters.electronic_invoicing) {
      params.electronic_invoicing = filters.electronic_invoicing
    }
    if (filters.is_favorite === true) {
      params.is_favorite = 'true'
    }

    if (filters.sortBy) {
      params.sort_by = filters.sortBy
    }
    if (filters.sortDirection) {
      params.sort_direction = filters.sortDirection
    }

    const result = await fetchFreelancers(params)

    if (result.code === 200) {
      return {
        items: result.data?.items || [],
        total: result.data?.total || 0,
        electronic_mandate_enabled: result.data?.electronic_mandate_enabled === true,
        electronic_invoicing_enabled: result.data?.electronic_invoicing_enabled === true,
      }
    }

    return { items: [], total: 0, electronic_mandate_enabled: false, electronic_invoicing_enabled: false }
  }
}

/**
 * Options de query pour prefetch ou useQuery externe.
 * Même clé / même queryFn que useFreelancersList pour partager le cache.
 */
export function getFreelancersListQueryOptions(filters: FreelancersListFilters) {
  return {
    queryKey: freelancersListKeys.list({
      status: filters.status,
      search: filters.search,
      tagFilter: filters.team_tag_id,
      referentFilter: filters.referent_id,
      teamId: filters.team_id,
      jobTitle: filters.job_title,
      teamUnitIds: filters.team_unit_ids,
      kycComplete: filters.kyc_complete,
      masterAgreement: filters.master_agreement,
      freelancerType: filters.freelancer_type,
      electronicInvoicing: filters.electronic_invoicing,
      isFavorite: filters.is_favorite,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
    }),
    queryFn: buildFreelancersListQueryFn(filters),
  }
}

/**
 * Hook pour récupérer la liste des freelancers
 */
export function useFreelancersList(
  filters: FreelancersListFilters,
  options?: { enabled?: boolean }
) {
  const apiReady = useTeamScopeApiReady()
  return useQuery({
    ...getFreelancersListQueryOptions(filters),
    enabled: apiReady && options?.enabled !== false,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
}
